<template>
  <div class="area-map-editor d-flex flex-column fill-height">
    <div class="area-map-editor__body d-flex flex-grow-1">
      <v-navigation-drawer
        v-if="showCreateDrawer"
        v-model="drawerCreateOpen"
        absolute
        left
        temporary
        hide-overlay
        stateless
        width="400"
        class="area-map-editor__drawer"
      >
        <div class="area-map-editor__drawer-inner d-flex flex-column fill-height">
          <v-toolbar flat dense class="flex-grow-0">
            <v-toolbar-title class="subtitle-2">{{ $t('area.form.createTitle') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon small @click="closeCreateDrawer()">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div class="area-map-editor__drawer-list flex-grow-1">
            <slot name="create-entity" />
          </div>
        </div>
      </v-navigation-drawer>

      <div
        class="area-map-editor__map flex-grow-1"
        :class="{
          'area-map-editor__map--drawing': drawMode,
          'area-map-editor__map--create-drawer': mapOverlayLeftInset > 0,
        }"
      >
        <l-map
          ref="mapRef"
          :zoom="zoom"
          :center="center"
          :options="{ zoomControl: true, doubleClickZoom: false }"
          style="height: 100%; width: 100%; border-radius: 8px"
          @ready="onMapReady"
        >
          <l-tile-layer :url="url" :attribution="attribution" />
          <l-control position="topright" class="area-map-editor__legend">
            <div class="d-flex align-center caption mb-1">
              <span class="area-map-editor__legend-dot" :style="{ background: coresSituacao.dentro }" />
              {{ $t('area.units.situation.inside') }}
            </div>
            <div class="d-flex align-center caption mb-1">
              <span class="area-map-editor__legend-dot" :style="{ background: coresSituacao.fora }" />
              {{ $t('area.units.situation.outside') }}
            </div>
            <div class="d-flex align-center caption mb-1">
              <span class="area-map-editor__legend-dot area-map-editor__legend-dot--hollow" />
              {{ $t('area.units.suggested') }}
            </div>
            <div class="d-flex align-center caption">
              <v-icon x-small :color="corZonaRisco" class="mr-1">{{ iconeZonaRisco }}</v-icon>
              {{ $t('area.riskZone') }}
            </div>
          </l-control>
        </l-map>
        <recursos-operacao-map-controls :items="controlItems" :style="controlsStyle" @select="onControlSelect" />
        <div
          v-if="drawMode || vertexEditMode"
          class="area-map-editor__editbar d-flex align-center flex-wrap"
          :style="editBarStyle"
        >
          <template v-if="drawMode">
            <span class="caption mr-2">{{ $t('area.map.drawHint', { count: draftVertices.length }) }}</span>
            <v-btn x-small text :disabled="!draftVertices.length" @click="undoVertex">
              <v-icon x-small left>mdi-undo</v-icon>{{ $t('area.map.undo') }}
            </v-btn>
            <v-btn x-small text color="primary" :disabled="draftVertices.length < 3" @click="finishDraw">
              <v-icon x-small left>mdi-check</v-icon>{{ $t('area.map.finish') }}
            </v-btn>
            <v-btn x-small text @click="setDrawPolygonMode(false)">{{ $t('button.cancel') }}</v-btn>
          </template>
          <template v-else>
            <span class="caption mr-2">{{ $t('area.map.editVerticesHint') }}</span>
            <v-btn x-small text color="primary" @click="setVertexEditMode(false)">
              <v-icon x-small left>mdi-check</v-icon>{{ $t('area.map.finish') }}
            </v-btn>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import { LMap, LTileLayer, LControl } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import RecursosOperacaoMapControls from "./RecursosOperacaoMapControls.vue";
import { verticesDaArea, geometriaParaSalvar, SITUACAO } from "@/utils/areasGeometria";
import {
  camadaDaArea,
  estiloArea,
  aplicarHachura,
  corSituacaoUnidade,
  COR_ZONA_RISCO,
  ICONE_ZONA_RISCO,
} from "@/utils/areasMapa";

/**
 * Mapa do cadastro de Áreas de Planejamento (módulo "areas" de Recursos Operacionais).
 * Desenha o polígono por cliques, edita vértices por arrasto e mostra as unidades da área
 * coloridas pela situação (dentro/fora) e as sugeridas (centro dentro, ainda não
 * associadas). Fala com o CRUD pelo mesmo evento `map-action` do editor de cercas.
 */
export default {
  name: "AreaMapEditor",
  components: { LMap, LTileLayer, LControl, RecursosOperacaoMapControls },
  props: {
    /** Áreas marcadas na tabela (exibidas juntas). */
    areas: { type: Array, default: () => [] },
    /** Área em edição (editedItem do CRUD): o polígono dela é o editável. */
    area: { type: Object, default: null },
    /** Unidades associadas com situacao_posicao. */
    unidades: { type: Array, default: () => [] },
    /** Unidades sugeridas (centro dentro, não associadas). */
    sugestoes: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: false },
    canInsert: { type: Boolean, default: false },
  },
  data() {
    return {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      zoom: 4,
      center: [-15.78, -47.93],
      map: null,
      drawerCreateOpen: false,
      drawMode: false,
      vertexEditMode: false,
      draftVertices: [],
      layers: { areas: null, editavel: null, unidades: null, desenho: null },
      lastFitKey: null,
      coresSituacao: { dentro: corSituacaoUnidade(SITUACAO.DENTRO), fora: corSituacaoUnidade(SITUACAO.FORA) },
      corZonaRisco: COR_ZONA_RISCO,
      iconeZonaRisco: ICONE_ZONA_RISCO,
    };
  },
  computed: {
    /** Largura do drawer de criação aberto: os overlays do mapa vão para a parte visível. */
    mapOverlayLeftInset() {
      return this.showCreateDrawer && this.drawerCreateOpen ? 400 : 0;
    },
    controlsStyle() {
      const inset = this.mapOverlayLeftInset;
      return inset ? { left: `${inset + 23}px` } : {};
    },
    editBarStyle() {
      const inset = this.mapOverlayLeftInset;
      if (!inset) return {};
      return {
        left: `calc(50% + ${inset / 2}px)`,
        maxWidth: `calc(100% - ${inset}px - 24px)`,
      };
    },
    showCreateDrawer() {
      return this.canInsert;
    },
    podeEditarGeometria() {
      return this.canEdit || (this.canInsert && this.drawerCreateOpen);
    },
    verticesEditaveis() {
      return verticesDaArea(this.area);
    },
    controlItems() {
      const items = [];
      if (this.canInsert) {
        items.push({ id: "create-area", icon: "mdi-plus", label: this.$t("area.form.createTitle"), active: this.drawerCreateOpen });
      }
      if (this.podeEditarGeometria && (this.area || this.drawerCreateOpen)) {
        items.push({
          id: "draw-area",
          icon: "mdi-vector-polygon",
          label: this.verticesEditaveis ? this.$t("area.map.redraw") : this.$t("area.map.draw"),
          active: this.drawMode,
        });
        if (this.verticesEditaveis) {
          items.push({
            id: "edit-vertices",
            icon: "mdi-vector-polyline-edit",
            label: this.$t("area.map.editVertices"),
            active: this.vertexEditMode,
          });
        }
      }
      if (this.areas.length || this.verticesEditaveis) {
        items.push({ id: "fit", icon: "mdi-fit-to-screen-outline", label: this.$t("area.map.fit") });
      }
      return items;
    },
  },
  watch: {
    areas: { deep: false, handler() { this.renderAll(); } },
    area: { deep: true, handler() { this.renderAll(); } },
    unidades: {
      deep: false,
      handler() {
        this.renderUnidades();
        if (!this.verticesEditaveis) this.fitAll(false);
      },
    },
    sugestoes: { deep: false, handler() { this.renderUnidades(); } },
  },
  activated() {
    this.$nextTick(() => this.map && this.map.invalidateSize());
  },
  beforeDestroy() {
    if (this.map) this.map.off("click", this.onMapClick);
  },
  methods: {
    onMapReady(map) {
      this.map = map;
      this.layers.areas = L.layerGroup().addTo(map);
      this.layers.editavel = L.layerGroup().addTo(map);
      this.layers.unidades = L.layerGroup().addTo(map);
      this.layers.desenho = L.layerGroup().addTo(map);
      map.on("click", this.onMapClick);
      this.renderAll();
      this.$nextTick(() => map.invalidateSize());
    },
    emitAction(payload) {
      this.$emit("map-action", Object.assign({ context: "areas" }, payload));
    },
    onControlSelect(id) {
      if (id === "create-area") {
        if (this.drawerCreateOpen) this.closeCreateDrawer();
        else this.openCreateDrawer();
      } else if (id === "draw-area") {
        this.setDrawPolygonMode(!this.drawMode);
      } else if (id === "edit-vertices") {
        this.setVertexEditMode(!this.vertexEditMode);
      } else if (id === "fit") {
        this.fitAll(true);
      }
    },
    openCreateDrawer({ silent } = {}) {
      if (!this.showCreateDrawer) return;
      const already = this.drawerCreateOpen;
      this.drawerCreateOpen = true;
      if (!silent && !already) this.emitAction({ action: "create-area" });
    },
    closeCreateDrawer({ silent } = {}) {
      const was = this.drawerCreateOpen;
      this.drawerCreateOpen = false;
      this.setDrawPolygonMode(false);
      this.setVertexEditMode(false);
      if (!silent && was) this.emitAction({ action: "close-create" });
    },
    setDrawPolygonMode(on) {
      const ativo = !!on && this.podeEditarGeometria;
      if (ativo) this.vertexEditMode = false;
      this.drawMode = ativo;
      this.draftVertices = [];
      this.renderDesenho();
      this.renderEditavel();
      this.emitAction({ action: "draw-polygon-mode", active: ativo });
    },
    setVertexEditMode(on) {
      this.vertexEditMode = !!on && this.podeEditarGeometria && !!this.verticesEditaveis;
      if (this.vertexEditMode) this.drawMode = false;
      this.renderEditavel();
    },
    onMapClick(e) {
      if (!this.drawMode) return;
      this.draftVertices.push({ lt: Number(e.latlng.lat.toFixed(7)), lg: Number(e.latlng.lng.toFixed(7)) });
      this.renderDesenho();
    },
    undoVertex() {
      this.draftVertices.pop();
      this.renderDesenho();
    },
    finishDraw() {
      if (this.draftVertices.length < 3) return;
      const vertices = this.draftVertices.slice();
      this.drawMode = false;
      this.draftVertices = [];
      this.renderDesenho();
      this.emitAction({ action: "draw-polygon-mode", active: false });
      this.emitAction({ action: "area-polygon-drawn", geometry: Object.assign({ vertices }, geometriaParaSalvar(vertices)) });
    },
    renderAll() {
      if (!this.map) return;
      this.renderAreas();
      this.renderEditavel();
      this.renderUnidades();
      this.fitAll(false);
    },
    idEditavel() {
      return this.area && this.area.id_area != null ? Number(this.area.id_area) : null;
    },
    renderAreas() {
      const grupo = this.layers.areas;
      if (!grupo) return;
      grupo.clearLayers();
      const idEditavel = this.idEditavel();
      this.areas.forEach((a) => {
        if (idEditavel != null && Number(a.id_area) === idEditavel) return;
        const camada = camadaDaArea(a, { rotuloZona: this.$t("area.riskZone") });
        if (camada) grupo.addLayer(camada);
      });
    },
    renderEditavel() {
      const grupo = this.layers.editavel;
      if (!grupo) return;
      grupo.clearLayers();
      const vertices = this.verticesEditaveis;
      if (!vertices || this.drawMode) return;
      if (!this.vertexEditMode) {
        const camada = camadaDaArea(this.area, { destaque: true, rotuloZona: this.$t("area.riskZone") });
        if (camada) grupo.addLayer(camada);
        return;
      }
      const latlngs = vertices.map((p) => [p.lt, p.lg]);
      const poligono = L.polygon(latlngs, estiloArea(this.area, { destaque: true }));
      grupo.addLayer(poligono);
      aplicarHachura(this.map, poligono, this.area);
      latlngs.forEach((ll, idx) => {
        const handle = L.marker(ll, {
          draggable: true,
          icon: L.divIcon({ className: "area-map-editor__vertex", iconSize: [12, 12], iconAnchor: [6, 6] }),
        });
        handle.on("drag", (ev) => {
          latlngs[idx] = [ev.latlng.lat, ev.latlng.lng];
          poligono.setLatLngs(latlngs);
          aplicarHachura(this.map, poligono, this.area);
        });
        handle.on("dragend", () => {
          const novos = latlngs.map((p) => ({ lt: Number(p[0].toFixed(7)), lg: Number(p[1].toFixed(7)) }));
          this.emitAction({ action: "area-polygon-changed", geometry: Object.assign({ vertices: novos }, geometriaParaSalvar(novos)) });
        });
        grupo.addLayer(handle);
      });
    },
    renderDesenho() {
      const grupo = this.layers.desenho;
      if (!grupo) return;
      grupo.clearLayers();
      if (!this.drawMode || !this.draftVertices.length) return;
      const latlngs = this.draftVertices.map((p) => [p.lt, p.lg]);
      grupo.addLayer(L.polygon(latlngs, { color: "#FB8C00", weight: 2, dashArray: "6 4", fillOpacity: 0.08 }));
      latlngs.forEach((ll) => grupo.addLayer(L.circleMarker(ll, { radius: 5, color: "#FB8C00", fillColor: "#fff", fillOpacity: 1, weight: 2 })));
    },
    renderUnidades() {
      const grupo = this.layers.unidades;
      if (!grupo) return;
      grupo.clearLayers();
      const nome = (u) => [u.cod_unidade, u.descricao_unidade].filter(Boolean).join(" - ");
      this.unidades.forEach((u) => {
        if (u.situacao_posicao === SITUACAO.SEM_CENTRO) return;
        const lat = Number(u.lat_centro);
        const lng = Number(u.lng_centro);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const cor = corSituacaoUnidade(u.situacao_posicao);
        const m = L.circleMarker([lat, lng], { radius: 7, color: "#fff", weight: 2, fillColor: cor, fillOpacity: 1 });
        m.bindTooltip(nome(u) + (u.situacao_posicao === SITUACAO.FORA ? " — " + this.$t("area.units.situation.outside") : ""));
        m.on("click", () => this.emitAction({ action: "unit-click", unidade: u }));
        grupo.addLayer(m);
      });
      this.sugestoes.forEach((u) => {
        const lat = Number(u.lat_centro);
        const lng = Number(u.lng_centro);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const m = L.circleMarker([lat, lng], {
          radius: 7, color: corSituacaoUnidade(SITUACAO.DENTRO), weight: 2, dashArray: "3 2", fillColor: "#fff", fillOpacity: 0.9,
        });
        m.bindTooltip(nome(u) + " — " + this.$t("area.units.suggestedClickToAdd"));
        m.on("click", () => this.emitAction({ action: "suggestion-click", unidade: u }));
        grupo.addLayer(m);
      });
    },
    fitAll(force) {
      if (!this.map) return;
      const pontos = [];
      const add = (a) => (verticesDaArea(a) || []).forEach((p) => pontos.push([p.lt, p.lg]));
      this.areas.forEach(add);
      add(this.area);
      // Sem polígono ainda (área a partir das unidades): enquadra os centros associados.
      if (this.area && !this.verticesEditaveis) {
        this.unidades.forEach((u) => {
          const lat = Number(u.lat_centro);
          const lng = Number(u.lng_centro);
          if (u.lat_centro != null && u.lat_centro !== "" && Number.isFinite(lat) && Number.isFinite(lng)) pontos.push([lat, lng]);
        });
      }
      const chave = pontos.length ? pontos.length + ":" + pontos[0].join(",") : null;
      if (!pontos.length || (!force && chave === this.lastFitKey)) return;
      this.lastFitKey = chave;
      this.map.fitBounds(L.latLngBounds(pontos), {
        paddingTopLeft: [24 + this.mapOverlayLeftInset, 24],
        paddingBottomRight: [24, 24],
        maxZoom: 15,
      });
    },
  },
};
</script>

<style scoped>
.area-map-editor {
  height: 100%;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}
.area-map-editor__body {
  min-height: 0;
  position: relative;
  overflow: hidden;
}
.area-map-editor__drawer {
  z-index: 1200;
}
.area-map-editor__drawer >>> .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.area-map-editor__drawer-inner {
  height: 100%;
  min-height: 0;
}
.area-map-editor__drawer-list {
  overflow-y: auto;
  min-height: 0;
}
.area-map-editor__map {
  min-height: 0;
  position: relative;
  overflow: hidden;
}
.area-map-editor__map--create-drawer >>> .leaflet-top.leaflet-left {
  left: 400px;
}
.area-map-editor__map--drawing >>> .leaflet-container {
  cursor: crosshair;
}
.area-map-editor__editbar {
  position: absolute;
  z-index: 1100;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 6px 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  max-width: calc(100% - 24px);
}
.area-map-editor__legend {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 6px;
  padding: 6px 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
.area-map-editor__legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  border: 1px solid #fff;
}
.area-map-editor__legend-dot--hollow {
  background: #fff;
  border: 2px dashed #2e7d32;
}
.area-map-editor >>> .area-map-editor__vertex {
  background: #fff;
  border: 2px solid #1565c0;
  border-radius: 50%;
  cursor: move;
}
</style>
